import { useState, useEffect } from 'react';
import styles from './EditarPaso.module.css';
import { FaArrowLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';
import {useAuth} from '../../Auth/AuthContext';

const EditarPaso = ({ pasoId, onBack, onPasoUpdated }) => {
    const [formData, setFormData] = useState({
        pa_numero: '',
        pa_titulo: '',
        pa_detalles: '',
        pa_imagen: '',
        pa_descripcion: '',
        pa_nom_subtitulo: '',
        pa_posicion_tablero: ''
    });
    const [originalData, setOriginalData] = useState({});
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        if (pasoId) {
            fetchTestData();
        }
    }, [pasoId]);

    const fetchTestData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/paso/${pasoId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                }
            });
            const data = await response.json();
            const paso = data[0];

            if (paso) {
                const processedData = {
                    pa_titulo: (paso.pa_titulo || '').trim(),
                    pa_detalles: (paso.pa_detalles || '').trim(),
                    pa_imagen: (paso.pa_imagen || '').trim(),
                    pa_descripcion: (paso.pa_descripcion || '').trim(),
                    pa_nom_subtitulo: (paso.pa_nom_subtitulo || '').trim(),
                };
                setFormData(processedData);
                setOriginalData(processedData);
            }
        } catch (error) {
            console.error('Error:', error);
            setError('Error de conexión al cargar el paso');
        } finally {
            setLoading(false);
        }
    };

    //Campos actualizados
    const getUpdatedFields = () => {
        const updates = {};
        
        for (const key in formData) {
            // Saltar la imagen si hay un archivo seleccionado O si queremos ignorar cambios en la imagen existente
            if (key === 'pa_imagen') {
                // Solo incluir pa_imagen si hay un archivo nuevo seleccionado
                if (selectedFile) {
                    continue; // El archivo se manejará por separado
                }
                // Si no hay archivo nuevo, comparar la imagen actual
                const currentValue = (formData[key] || '').toString().trim();
                const originalValue = (originalData[key] || '').toString().trim();
                
                if (currentValue !== originalValue) {
                    updates[key] = formData[key];
                }
                continue;
            }
            
            // Manejar diferentes tipos de datos
            let currentValue, originalValue;
            
            if (typeof formData[key] === 'string') {
                currentValue = (formData[key] || '').trim();
                originalValue = (originalData[key] || '').trim();
            } else {
                // Para números u otros tipos, convertir a string para comparar
                currentValue = (formData[key] || '').toString();
                originalValue = (originalData[key] || '').toString();
            }
            
            if (currentValue !== originalValue) {
                // Guardar el valor original sin procesar
                updates[key] = typeof formData[key] === 'string' ? formData[key].trim() : formData[key];
            }
        }
        
        return updates;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo de archivo
            const allowedTypes = ['image/png'];
            if (!allowedTypes.includes(file.type)) {
                setError('Solo se permiten archivos .png');
                return;
            }
            
            // Validar tamaño (5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('El archivo debe ser menor a 5MB');
                return;
            }
            
            setSelectedFile(file);
            setError(''); // Limpiar errores previos
        }
    };

    // Función para verificar si hay imagen (útil para validaciones)
    const hasImage = () => {
        return formData.pa_imagen || selectedFile;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar que tenga imagen (actual o nueva)
        if (!hasImage()) {
            alert('Debe seleccionar una imagen para el paso');
            return;
        }

        // Validar campos obligatorios
        if (!formData.pa_titulo.trim() || !formData.pa_detalles.trim() || !formData.pa_descripcion.trim()) {
            setError('Los campos título, detalles y descripción son obligatorios');
            return;
        }

        const updated = getUpdatedFields();
        const hasFileUpdate = selectedFile !== null;
        
        // Verificar si hay cambios
        if (Object.keys(updated).length === 0 && !hasFileUpdate) {
            setError('No hiciste ningún cambio para actualizar.');
            return;
        }

        setSaving(true);
        
        try {
            setError('');
            const formDataToSend = new FormData();
            
            // Si hay cambios en campos Y archivo nuevo
            if (Object.keys(updated).length > 0 && hasFileUpdate) {
                // Agregar todos los campos actualizados
                Object.keys(updated).forEach(key => {
                    const value = updated[key];
                    if (value !== null && value !== undefined && value.toString().trim() !== '') {
                        formDataToSend.append(key, value.toString().trim());
                    }
                });
                // Agregar el archivo nuevo
                formDataToSend.append('pa_imagen', selectedFile);
            }
            // Si solo hay cambios en campos (sin archivo nuevo)
            else if (Object.keys(updated).length > 0 && !hasFileUpdate) {
                Object.keys(updated).forEach(key => {
                    const value = updated[key];
                    if (value !== null && value !== undefined && value.toString().trim() !== '') {
                        formDataToSend.append(key, value.toString().trim());
                    }
                });
            }
            // Si solo hay archivo nuevo (sin cambios en campos)
            else if (hasFileUpdate && Object.keys(updated).length === 0) {
                // Agregar todos los campos obligatorios para que el backend no falle
                formDataToSend.append('pa_titulo', formData.pa_titulo.trim());
                formDataToSend.append('pa_detalles', formData.pa_detalles.trim());
                formDataToSend.append('pa_descripcion', formData.pa_descripcion.trim());
                
                // Agregar campos opcionales si existen
                if (formData.pa_nom_subtitulo && formData.pa_nom_subtitulo.trim()) {
                    formDataToSend.append('pa_nom_subtitulo', formData.pa_nom_subtitulo.trim());
                }
                
                // Agregar el archivo nuevo
                formDataToSend.append('pa_imagen', selectedFile);
            }

            // Hacer la petición
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/actualizar-paso/${pasoId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formDataToSend,
            });

            if (!response.ok) {
                const text = await response.text();
                
                try {
                    const data = JSON.parse(text);
                    Swal.fire({
                        title: 'Error al actualizar', 
                        text: data.message || 'Ocurrió un error en el servidor', 
                        icon: 'error',
                        showConfirmButton: false, 
                        confirmButtonColor: '#242b57',
                        customClass: {
                            popup: 'popupbonito',
                        },
                    });
                    setError(data.message || 'Error al actualizar el paso');
                } catch (parseError) {
                    console.error('No es JSON válido:', text);
                    Swal.fire({
                        title: 'Error del servidor', 
                        text: 'Respuesta no válida del servidor', 
                        icon: 'error',
                        showConfirmButton: false,
                        customClass: {
                            popup: 'popupbonito',
                        },
                    });
                    setError('Error del servidor');
                }
                return;
            }

            const data = await response.json();
            
            Swal.fire({
                title: '¡Paso actualizado!',
                text: data.message || 'El paso se ha actualizado correctamente',
                icon: 'success',
                timer: 3000,
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito',
                },
            }).then(() => {
                onPasoUpdated(); // Callback para actualizar la lista de pasos
                onBack(); // Volver a la vista principal
            });
            
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                title: 'Error de conexión', 
                text: 'No se pudo conectar con el servidor', 
                icon: 'error',
                showCancelButton: false,
                customClass: {
                    popup: 'popupbonito',
                },
            });
            setError('Error de conexión al actualizar el paso');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando sección...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.formContainerPaso}>
            <div className={styles.headerSection}>
            <button className={styles.btnBack} onClick={onBack}>
                <FaArrowLeft className={styles.flechaVolver} />
                Volver a pasos
            </button>
            <h1 className={styles.titulo}>Editar Paso</h1>
            </div>

            <form className={styles.pasoForm}
                onSubmit={handleSubmit} >
                <div className={styles.formBody}>
                    <div className={styles.formSection}>
                        <h3>Información de Presentación</h3>
                        <div className={styles.formEdit}>
                            <label htmlFor="pa_titulo">
                                <i/>
                                Título del Paso
                            </label>
                            <input
                                type="text"
                                id="pa_titulo"
                                name="pa_titulo"
                                value={formData.pa_titulo}
                                onChange={handleInputChange}
                                placeholder="Ej: Mis Desicioness"
                                required
                            />
                        </div>
                        <div className={styles.formEdit}>
                            <label htmlFor="pa_detalles">
                                <i/>
                                Detalles del paso
                            </label>
                            <textarea
                                id="pa_detalles"
                                name="pa_detalles"
                                value={formData.pa_detalles}
                                onChange={handleInputChange}
                                placeholder="Escribe de forma resumida el contenido del paso"
                                rows="4"
                                required
                            />
                        </div>
                        <div className={styles.formEdit}>
                            <label htmlFor="pa_nom_subtitulo">
                                <i/>
                                Subtítulo (Título para las secciones)
                            </label>
                            <input
                                type="text"
                                id="pa_nom_subtitulo"
                                name="pa_nom_subtitulo"
                                value={formData.pa_nom_subtitulo}
                                onChange={handleInputChange}
                                placeholder="Ej: Inteligencias"
                                required
                            />
                        </div>
                        <div className={styles.formEdit}>
                            <label htmlFor="pa_descripcion">
                                <i/>
                                Descripción (Texto detallado acerca del paso)
                            </label>
                            <textarea
                                id="pa_descripcion"
                                name="pa_descripcion"
                                value={formData.pa_descripcion}
                                onChange={handleInputChange}
                                placeholder="Escribe de forma detallada el contenido del paso"
                                rows="6"
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <h3>Diseño</h3>
                        <div className={styles.formDisenioEdit}>
                            <label htmlFor="pa_imagen">
                            Selecciona una imagen para el Paso
                            <span className={styles.textAyuda}> Elija archivos PNG </span>
                            <input 
                                type="file"
                                id="pa_imagen_file"
                                name="pa_imagen_file"
                                accept="image/png"
                                onChange={handleImageChange}
                            />
                            </label>
                            {/* Mostrar imagen actual si existe */}
                            {formData.pa_imagen && !selectedFile && (
                                <div className={styles.mt2}>
                                    <span className={styles.textAyuda}>Imagen actual del Paso:</span>

                                    <img 
                                        src={formData.pa_imagen.startsWith('http') 
                                            ? formData.pa_imagen 
                                            : `${process.env.REACT_APP_API_URL}/pasos/paso-images/${formData.pa_imagen}`
                                        } 
                                        alt="Vista previa" 
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                            
                            {/* Vista previa del archivo seleccionado */}
                            {selectedFile && (
                                <div className={styles.mt2}>
                                    <small>Nueva imagen:</small>
                                    <img 
                                        src={URL.createObjectURL(selectedFile)} 
                                        alt="Vista previa nueva" 
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button type="submit" className={styles.btnSubmit} disabled={saving}>
                        {saving ? (
                            <>
                                <div className={styles.btnSpinner}></div>
                                Actualizando...
                            </>
                        ) : (
                            <>
                                <i></i>
                                Actualizar Paso
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditarPaso;